"use client";

import { useState } from "react";
import type { ComponentProps, ReactNode } from "react";
import { TRPCClientError } from "@trpc/client";
import { Button } from "./Button";
import { createLazyOverlay } from "./createLazyOverlay";
import type { DialogPanel as DialogPanelComponent } from "./DialogPanel";
import { reportClientError } from "@/lib/clientReporting";
export const Dialog = createLazyOverlay<ComponentProps<typeof DialogPanelComponent>>({
  name: "dialog",
  load: () => import("./DialogPanel"),
  exportName: "DialogPanel",
  renderFallback: () => null
});
export function ConfirmDialog({
  isOpen,
  onClose,
  title,
  description,
  children,
  cancelLabel,
  confirmLabel,
  onConfirm,
  isDestructive,
  isBusy,
  isConfirmDisabled
}: {
  isOpen: boolean;
  onClose: () => void;
  title: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
  cancelLabel: ReactNode;
  confirmLabel: ReactNode;
  onConfirm: () => void | Promise<unknown>;
  isDestructive?: boolean;
  isBusy?: boolean;
  isConfirmDisabled?: boolean;
}) {
  const [isConfirming, setIsConfirming] = useState(false);
  const isWorking = Boolean(isBusy) || isConfirming;
  const confirm = async () => {
    setIsConfirming(true);
    try {
      await onConfirm();
      onClose();
    } catch (cause) {
      // A tRPC mutation failure already reaches Sentry via MutationCache
      // (or is an expected, caller-handled rejection); only an
      // onConfirm failure outside the mutation itself is reported here.
      if (!(cause instanceof TRPCClientError)) {
        reportClientError({
          message: cause instanceof Error ? cause.message : "ConfirmDialog: onConfirm failed",
          cause: cause instanceof Error ? cause : null,
          tags: {
            surface: "confirmDialog"
          }
        });
      }
    } finally {
      setIsConfirming(false);
    }
  };
  return <Dialog isOpen={isOpen} onClose={() => {
    if (!isWorking) onClose();
  }} title={title} description={description} footer={<>
                    <Button variant="outline" size="sm" onClick={onClose} disabled={isWorking}>
                        {cancelLabel}
                    </Button>
                    <Button variant={isDestructive ? "destructive" : "primary"} size="sm" disabled={isWorking || Boolean(isConfirmDisabled)} aria-busy={isConfirming || undefined} onClick={() => void confirm()}>
                        {confirmLabel}
                    </Button>
                </>} data-sentry-element="Dialog" data-sentry-component="ConfirmDialog" data-sentry-source-file="Dialog.tsx">
            {children}
        </Dialog>;
}